import { hasPermission } from "../auth.js";
import { openDialog } from "../../components/features/app-dialog/utils/core/dialogManager.js";
import { apiFetch } from "../apiClient.js";
import { enableCustomer } from "../../components/features/app-table/tableManager/customerManager.js";
import { dashboardModules, permittedLanguageNames } from "../utils/enums.js";
import { deepLink } from "../utils/deepLink.js";

function formatHours(decimal) { //formats 2.5 to 2:30
    const total = Math.round(Number(decimal) * 60);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function isSafeUrl(url) {
    const safeUrl = String(url ?? '').toLowerCase();
    return safeUrl.startsWith('http://') || safeUrl.startsWith('https://');
}

function buildView(content) {
    const container = document.createElement("div");
    container.id = "customers-view";

    const list = document.createElement("app-panel-list");
    const detail = document.createElement("app-panel-detail");

    container.appendChild(list);
    container.appendChild(detail);
    content.appendChild(container);

    return { list, detail };
}

export async function mountCustomersView(content, { selectedId = null } = {}) {
    const { list, detail } = buildView(content);

    const getListName = customer => customer.shortName || customer.name;

    list.configure({
        title: 'KUNDEN',
        endpoint: 'Customers?summary=true',
        getName: getListName,
        getSubtitle: customer => customer.applicationCount === 1 ? '1 Applikation' : `${customer.applicationCount} Applikationen`,
        getIsDisabled: customer => customer.isDeleted,
        dataChangedEvent: 'customer-data-changed',
        initialSelectedId: selectedId,
            onSelect: (item) => {
            detail.load(item);
            const query = window.location.hash.split('?')[1];
            history.replaceState({}, '', `#customers/${item.id}${query ? `?${query}` : ''}`);
        },
        onDeselect: () => {
            detail.clear();
            const query = window.location.hash.split('?')[1];
            history.replaceState({}, '', `#customers${query ? `?${query}` : ''}`);
        },
        actions: [
            {
                label: 'Als Vorlage verwenden', icon: 'fa-copy',
                permission: () => hasPermission('Customers.Create'),
                showFor: item => !item.isDeleted,
                handler: async (item) => {
                    const customer = await apiFetch(`Customers/${item.id}`);
                    openDialog('createCustomer', {
                        language: customer?.language,
                        templateApplications: (customer?.applications ?? []).map(application => ({ applicationId: application.id ?? application.applicationId, url: application.url ?? '' })),
                        templateServices: (customer?.services ?? []).map(service => ({ id: service.bookedServiceId, isTranslationActive: !!service.isTranslationActive })),
                        dashboardModules: customer?.dashboardModules ?? [],
                        permittedLanguages: customer?.permittedLanguages ?? [],
                    });
                },
            },
            {
                label: 'Löschen', icon: 'fa-trash', danger: true,
                permission: () => hasPermission('Customers.Delete'),
                showFor: item => !item.isDeleted,
                handler: (item) => openDialog('deleteCustomer', item),
            },
            {
                label: 'Reaktivieren', icon: 'fa-rotate-left',
                permission: () => hasPermission('Customers.Enable'),
                showFor: item => item.isDeleted,
                handler: (item) => enableCustomer(item.id),
            },
        ],
        onDataChanged: (listEl, e) => {
            const { action, customer, id, applications } = e.detail ?? {};
            if (action === 'created' && customer?.id) {
                listEl.addItem(customer);
                listEl.selectItem(customer.id, { silent: true });
                listEl.flashItem(customer.id, 'create');
            } else if (action === 'updated' && customer?.id) {
                listEl.updateItem(customer.id, item => ({ ...item, name: customer.shortName || customer.name || item.name }));
                listEl.flashItem(customer.id, 'update');
            } else if (action === 'enabled' && id) {
                listEl.updateItem(id, item => ({ ...item, isDeleted: false }));
                listEl.flashItem(id, 'update');
            } else if (action === 'deleted' && id && customer) {
                listEl.updateItem(id, item => ({ ...item, isDeleted: true }));
                listEl.flashItem(id, 'update');
            } else if (action === 'applications-updated' && customer?.id) {
                listEl.updateItem(customer.id, item => ({ ...item, applicationCount: applications?.length ?? 0 }));
                listEl.flashItem(customer.id, 'update');
            }
        },
    });

    detail.configure({
        emptyText: 'Kunden auswählen',
        subLabel: 'Kunde',
        getName: data => data?.name,
        getAvatarStr: getListName,
        canEdit: () => false,
        canDelete: () => false,
        dataChangedEvent: 'customer-data-changed',
        loadData: async (item) => apiFetch(`Customers/${item.id}`),
        renderCards: (data) => {
            const canUpdateKeydata            = hasPermission('Customers.Update.Keydata');
            const canUpdateApplication        = hasPermission('Customers.Update.Applications');
            const canUpdateServices           = hasPermission('Customers.Update.Services');
            const canUpdateDashboardModules   = hasPermission('Customers.Update.Dashboard_modules');
            const canUpdatePermittedLanguages = hasPermission('Customers.Update.Permitted_languages');
            return [
                { title: 'STAMMDATEN', rows: [
                    { label: 'Name',      value: data?.name },
                    { label: 'Kurzname',  value: data?.shortName },
                    { label: 'Sprache',   value: permittedLanguageNames[data?.language] ?? '—' },
                ], directEdit: canUpdateKeydata ? { handler: () => openDialog('editCustomer', detail.currentData) } : undefined },
                { title: 'ZEITERFASSUNG', stats: [
                    { value: formatHours(data?.totalHours ?? 0), label: 'Stunden gesamt' },
                    { value: data?.totalEntries ?? 0,            label: 'Einträge' },
                ], onClick: deepLink('Entries.Read.Any', `#entries?scope=all&col-customerId=${detail.currentItem?.id}&colName-customerId=${encodeURIComponent(getListName(detail.currentItem ?? {}))}`) },
                {
                    title: 'APPLIKATIONEN',
                    rows: (data?.applications ?? []).map(application => ({
                        label: application.name,
                        primary: true,
                        value: application.url
                            ? (isSafeUrl(application.url)
                                ? `<a class="row-link" href="${application.url}" target="_blank" rel="noopener noreferrer"><i class="fa-solid fa-link"></i> ${application.url}</a>`
                                : application.url)
                            : '—',
                        onClick: deepLink(
                            'Entries.Read.Any',
                            `#entries?scope=all&col-customerId=${detail.currentItem?.id}&colName-customerId=${encodeURIComponent(getListName(detail.currentItem ?? {}))}&col-applicationId=${application.id}&colName-applicationId=${encodeURIComponent(application.name)}`,
                            application.id != null && detail.currentItem?.id != null
                        ),
                    })),
                    emptyHint: 'Keine Applikationen',
                    onClick: deepLink('Applications.Read', '#applications'),
                    directEdit: canUpdateApplication ? { handler: () => openDialog('editCustomerApplications', { id: detail.currentItem?.id, applications: detail.currentData?.applications ?? [] }) } : undefined },
                {
                    title: 'SERVICES',
                    rows: (data?.services ?? []).map(service => ({
                        label: service.bookedServiceName + (service.isTranslationActive ? ' <i class="fa-solid fa-language" style="opacity:0.55;font-size:0.85em"></i>' : ''),
                        primary: true,
                        value: service.url
                            ? (isSafeUrl(service.url)
                                ? `<a class="row-link" href="${service.url}" target="_blank" rel="noopener noreferrer"><i class="fa-solid fa-link"></i> ${service.url}</a>`
                                : service.url)
                            : '—',
                        onClick: deepLink('Services.Read', `#services?col-name=${encodeURIComponent(service.bookedServiceName)}`, !!service.bookedServiceName),
                    })),
                    emptyHint: 'Keine Services',
                    onClick: deepLink('Services.Read', '#services'),
                    directEdit: canUpdateServices ? { handler: () => openDialog('editCustomerServices', { id: detail.currentItem?.id, services: detail.currentData?.services ?? [] }) } : undefined,
                },
                {
                    title: 'PROJEKTE',
                    chips: (data?.projects ?? []).map(project => ({
                        name: project.name,
                        inactive: !project.isActive,
                        onClick: deepLink(
                            'Entries.Read.Any',
                            `#entries?scope=all&col-customerId=${detail.currentItem?.id}&colName-customerId=${encodeURIComponent(getListName(detail.currentItem ?? {}))}&col-projectId=${project.id}&colName-projectId=${encodeURIComponent(project.name)}`,
                            project.id != null && detail.currentItem?.id != null
                        ),
                    })),
                    emptyHint: 'Keine Projekte',
                    onClick: deepLink('Projects.Read', '#projects') },
                {
                    group: [
                        {
                            title: 'DASHBOARD MODULE',
                            chips: (data?.dashboardModules ?? []).map(id => dashboardModules[id]).filter(Boolean).map(name => ({ name })),
                            emptyHint: 'Keine Module',
                            directEdit: canUpdateDashboardModules ? { handler: () => openDialog('editCustomerDashboardModules', { id: detail.currentItem?.id, dashboardModules: detail.currentData?.dashboardModules ?? [] }) } : undefined,
                        },
                        {
                            title: 'ERLAUBTE SPRACHEN',
                            chips: (data?.permittedLanguages ?? []).map(id => permittedLanguageNames[id]).filter(Boolean).map(name => ({ name })),
                            emptyHint: 'Keine Sprachen',
                            directEdit: canUpdatePermittedLanguages ? { handler: () => openDialog('editCustomerPermittedLanguages', { id: detail.currentItem?.id, permittedLanguages: detail.currentData?.permittedLanguages ?? [] }) } : undefined,
                        },
                    ],
                },
            ];
        },
        onDataChanged: (detail, e) => {
            const { action, customer, id, applications, projects, services, dashboardModules: updatedDashboardModules, permittedLanguages: updatedPermittedLanguages, detailedCustomer, serviceId } = e.detail ?? {};
            if (action === 'created' && customer?.id) {
                detail.setData(detailedCustomer, customer);
                return;
            }
            if (!detail.currentItem) return;
            if (action === 'deleted' && id === detail.currentItem.id) detail.clear();
            else if (action === 'updated' && customer?.id === detail.currentItem.id)
                detail.setData({ ...detail.currentData, name: customer.name, shortName: customer.shortName, language: customer.language });
            else if (action === 'applications-updated' && customer?.id === detail.currentItem.id)
                detail.setData({ ...detail.currentData, applications: applications ?? [], projects: projects ?? detail.currentData?.projects ?? [] });
            else if (action === 'services-updated' && customer?.id === detail.currentItem.id)
                detail.setData({ ...detail.currentData, services: services ?? [] });
            else if (action === 'serviceConfiguration-updated' && customer?.id === detail.currentItem.id)
                detail.setData({
                    ...detail.currentData,
                    services: (detail.currentData?.services ?? []).map(s =>
                        s.bookedServiceId === serviceId ? { ...s, hasConfiguration: true } : s),
                });
            else if (action === 'dashboardModules-updated' && customer?.id === detail.currentItem.id)
                detail.setData({ ...detail.currentData, dashboardModules: updatedDashboardModules ?? [] });
            else if (action === 'permittedLanguages-updated' && customer?.id === detail.currentItem.id)
                detail.setData({ ...detail.currentData, permittedLanguages: updatedPermittedLanguages ?? [] });
        },
    });
}